// Configuracion del API
// API_BASE_URL es solo el dominio base (ej: https://vinson.com.ar o http://127.0.0.1:8000)
// Los endpoints siempre deben ser rutas relativas que empiecen con / (ej: /api/auth/login/)

/**
 * Función centralizada para obtener la URL base del API
 * Prioridad:
 * 1. En desarrollo: SIEMPRE localhost (evita pegar a entornos legacy remotos)
 * 2. En producción: REACT_APP_API_URL si existe
 * 3. En producción sin variable: window.location.origin
 */
const getApiBaseUrl = () => {
  // En desarrollo local, forzar backend local para evitar apuntar al CRM viejo.
  if (process.env.NODE_ENV === 'development') {
    return 'http://127.0.0.1:8000';
  }

  // En produccion, permitir override por variable.
  if (process.env.REACT_APP_API_URL) {
    const url = process.env.REACT_APP_API_URL.trim();
    // Remover /api si esta al final (se agregara automaticamente cuando corresponda)
    return url.replace(/\/api\/?$/, '');
  }

  // Fallback en produccion: usar mismo origen del frontend.
  if (process.env.NODE_ENV === 'production' && typeof window !== 'undefined') {
    return window.location.origin;
  }

  // Fallback defensivo.
  return 'http://127.0.0.1:8000';
};

const API_BASE_URL = getApiBaseUrl();

let backendEnvHint = null;
let backendEnvHintPromise = null;

const detectBackendEnvironment = async () => {
  if (backendEnvHint || backendEnvHintPromise || typeof window === 'undefined') {
    return backendEnvHintPromise || backendEnvHint;
  }
  backendEnvHintPromise = fetch(`${API_BASE_URL}/health`, {
    method: 'GET',
    headers: { 'Accept': 'application/json' },
  })
    .then(async (response) => {
      const contentType = response.headers.get('content-type') || '';
      if (!response.ok || !contentType.includes('application/json')) {
        return null;
      }
      const data = await response.json();
      if (data && typeof data === 'object') {
        backendEnvHint = {
          environment: data.environment || null,
          settingsModule: data.settings_module || null,
        };
        if (process.env.NODE_ENV === 'development' && (backendEnvHint.environment || backendEnvHint.settingsModule)) {
          console.log('✓ Backend settings detectado:', backendEnvHint);
        }
      }
      return backendEnvHint;
    })
    .catch(() => null)
    .finally(() => {
      backendEnvHintPromise = null;
    });
  return backendEnvHintPromise;
};

if (typeof window !== 'undefined') {
  detectBackendEnvironment();
}

const buildFullUrl = (endpoint) => {
  const normalizedEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  let apiPrefix = '';
  if (!normalizedEndpoint.startsWith('/api/')) {
    const isProduction = process.env.NODE_ENV === 'production';
    const hasWindow = typeof window !== 'undefined';
    const isDifferentOrigin = hasWindow && API_BASE_URL !== window.location.origin;
    if (isProduction || isDifferentOrigin) {
      apiPrefix = '/api';
    }
  }
  return `${API_BASE_URL}${apiPrefix}${normalizedEndpoint}`;
};

const refreshAccessToken = async () => {
  const refreshToken = localStorage.getItem('refresh_token');
  if (!refreshToken) return null;
  try {
    const url = buildFullUrl('/auth/refresh/');
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });
    if (!response.ok) return null;
    const data = await response.json();
    if (data?.access_token) {
      localStorage.setItem('token', data.access_token);
      if (data.refresh_token) {
        localStorage.setItem('refresh_token', data.refresh_token);
      }
      return data.access_token;
    }
  } catch (error) {
    return null;
  }
  return null;
};

// Verificar configuración al cargar (solo en desarrollo)
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  console.log('=== API Configuration ===');
  console.log('API_BASE_URL:', API_BASE_URL);
  console.log('REACT_APP_API_URL env:', process.env.REACT_APP_API_URL);
  console.log('Current origin:', window.location.origin);
}

// Verificar configuración en producción también (para debugging)
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'production') {
  console.log('=== API Configuration (Production) ===');
  console.log('API_BASE_URL:', API_BASE_URL);
  console.log('REACT_APP_API_URL env:', process.env.REACT_APP_API_URL || 'not set (using auto-detection)');
  console.log('Current origin:', window.location.origin);
  console.log('Detected API URL:', API_BASE_URL);
}

// Función helper para limpiar objetos antes de serializar (definida fuera para que esté disponible)
function cleanObjectForJSON(obj) {
    if (obj === null || obj === undefined) return null;
    if (typeof obj !== 'object') return obj;
    if (obj instanceof Date) return obj.toISOString();
    if (Array.isArray(obj)) {
      return obj.map(item => cleanObjectForJSON(item));
    }
    
    const cleaned = {};
    const seen = new WeakSet();
    
    function clean(value) {
      if (value === null || value === undefined) return null;
      if (typeof value !== 'object') return value;
      if (value instanceof Date) return value.toISOString();
      if (value instanceof RegExp) return value.toString();
      if (seen.has(value)) return '[Circular]';
      if (typeof value.nodeType === 'number') return '[DOM Element]';
      if (typeof value === 'function') return '[Function]';
      
      seen.add(value);
      
      if (Array.isArray(value)) {
        return value.map(item => clean(item));
      }
      
      const result = {};
      for (const key in value) {
        if (key.startsWith('_')) continue; // Ignorar propiedades privadas de React
        try {
          result[key] = clean(value[key]);
        } catch (e) {
          continue; // Omitir propiedades problemáticas
        }
      }
      seen.delete(value);
      return result;
    }
    
    return clean(obj);
  }

// Función helper para hacer peticiones al API
export const apiRequest = async (endpoint, options = {}) => {
  // Si skipAuth es true, no agregar token (para endpoints públicos)
  const skipAuth = options.skipAuth === true;
  const token = skipAuth ? null : localStorage.getItem('token');
  
  const method = options.method || 'GET';
  const isFormData = options.body instanceof FormData;
  const headers = {
    // No establecer Content-Type para FormData, el navegador lo hará automáticamente con el boundary
    ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
    ...(token && { Authorization: `Bearer ${token}` }),
    ...options.headers,
  };

  let body = options.body;
  // Si hay body, convertirlo a JSON (excepto si es FormData)
  if (body && typeof body === 'object' && !(body instanceof FormData)) {
    try {
      body = JSON.stringify(body);
    } catch (error) {
      // Si hay error de serialización circular, limpiar el objeto primero
      console.error('Error serializando body, limpiando objeto...', error);
      const cleanBody = cleanObjectForJSON(body);
      body = JSON.stringify(cleanBody);
    }
  }

  // Asegurar que endpoint empiece con /
  const normalizedEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  
  // Agregar prefijo /api cuando el backend corre bajo /api (prod y dev remoto)
  // En desarrollo local, solo agregar /api si el backend no es el mismo origen del frontend
  let apiPrefix = '';
  if (!normalizedEndpoint.startsWith('/api/')) {
    const isProduction = process.env.NODE_ENV === 'production';
    const hasWindow = typeof window !== 'undefined';
    const isDifferentOrigin = hasWindow && API_BASE_URL !== window.location.origin;
    if (isProduction || isDifferentOrigin) {
      // Solo agregar /api si no está ya presente
      apiPrefix = '/api';
    }
  }
  
  const fullUrl = `${API_BASE_URL}${apiPrefix}${normalizedEndpoint}`;
  
  // Validar que la URL sea válida antes de hacer la petición
  if (!fullUrl || !fullUrl.startsWith('http')) {
    throw new Error(`URL inválida: ${fullUrl}. Verifica que API_BASE_URL esté configurado correctamente.`);
  }
  
  // Verificar conectividad básica en desarrollo (solo para diagnóstico)
  // En producción, no ejecutar verificaciones de diagnóstico
  if (process.env.NODE_ENV === 'development' && typeof window !== 'undefined') {
    // Intentar hacer una petición HEAD simple para verificar conectividad
    // Esto es solo para diagnóstico, no afecta la petición real
    try {
      const testUrl = `${API_BASE_URL}/docs`;
      fetch(testUrl, { method: 'HEAD', mode: 'no-cors' }).catch(() => {
        // Ignorar errores de no-cors, solo es para verificar conectividad
      });
    } catch (e) {
      // Ignorar errores de verificación
    }
  }

  // Logging detallado para debugging (solo en desarrollo)
  if (process.env.NODE_ENV === 'development') {
    console.log('=== API Request ===');
    console.log('Method:', method);
    console.log('URL:', fullUrl);
    console.log('Endpoint:', endpoint);
    console.log('Has Token:', !!token);
    console.log('Token (first 20 chars):', token ? token.substring(0, 20) + '...' : 'none');
    console.log('Body:', body ? (typeof body === 'string' ? JSON.parse(body) : body) : 'none');
    console.log('Headers:', { ...headers, Authorization: token ? 'Bearer ***' : 'none' });
  }
  
  // Verificar que tenemos token para endpoints que lo requieren (solo en desarrollo)
  if (process.env.NODE_ENV === 'development' && !skipAuth && !token) {
    console.warn('⚠️ No hay token disponible para la petición a:', endpoint);
    // No lanzar error aquí, dejar que el backend responda con 401
  }

  // Logging para debugging (solo en desarrollo)
  if (process.env.NODE_ENV === 'development' && endpoint.includes('/companies')) {
    console.log('=== API Request (Companies) ===');
    console.log('URL:', fullUrl);
    console.log('Method:', method);
    console.log('Headers:', { ...headers, Authorization: token ? 'Bearer ***' : 'none' });
    if (body) {
      console.log('Body:', typeof body === 'string' ? JSON.parse(body) : body);
    }
  }

  try {

    // Agregar timeout y mejor manejo de errores de red
    // Para login, usar timeout más corto (5 segundos) ya que debería ser rápido
    const isLoginRequest = normalizedEndpoint.includes('/auth/login/');
    const timeoutDuration = isLoginRequest ? 5000 : 30000; // 5 segundos para login, 30 para otros
    
    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      if (process.env.NODE_ENV === 'development') {
        console.warn(`Timeout: Abortando petición después de ${timeoutDuration/1000} segundos`);
        console.warn('El backend no está respondiendo. Verifica que esté corriendo.');
      }
      controller.abort();
    }, timeoutDuration);
    
    // Agregar modo 'cors' explícitamente para evitar problemas
    const fetchOptions = {
      method: method,
      headers: headers,
      body: body,
      signal: controller.signal,
      mode: 'cors',  // Asegurar que use CORS
      credentials: 'include',  // Incluir credenciales para CORS con allow_credentials=True
      cache: 'no-cache'  // No cachear peticiones
    };
    
    // Logging adicional para diagnóstico (solo en desarrollo)
    if (process.env.NODE_ENV === 'development' && normalizedEndpoint.includes('/auth/login/')) {
      console.log('🔍 Detalles de la petición de login:');
      console.log('  URL completa:', fullUrl);
      console.log('  Headers enviados:', {
        'Content-Type': headers['Content-Type'],
        'Authorization': headers['Authorization'] ? 'Bearer ***' : 'none'
      });
      console.log('  Body:', typeof body === 'string' ? JSON.parse(body) : body);
      console.log('  Mode:', fetchOptions.mode);
      console.log('  Credentials:', fetchOptions.credentials);
    }
    
      // Intentar la petición
      let response;
      try {
        // Logging solo en desarrollo
        if (process.env.NODE_ENV === 'development') {
          console.log('=== Iniciando petición ===');
          console.log('URL completa:', fullUrl);
          console.log('Método:', method);
          console.log('Endpoint:', endpoint);
          console.log('Headers:', Object.keys(headers));
          console.log('Tiene body:', !!body);
          console.log('Token disponible:', !!token);
          console.log('Skip Auth:', skipAuth);
          console.log('Mode:', fetchOptions.mode);
          console.log('Credentials:', fetchOptions.credentials);
        }
        
        const fetchStartTime = process.env.NODE_ENV === 'development' ? Date.now() : null;
        response = await fetch(fullUrl, fetchOptions);
        clearTimeout(timeoutId);
        const fetchDuration = fetchStartTime ? Date.now() - fetchStartTime : null;
        
        // Logging solo en desarrollo
        if (process.env.NODE_ENV === 'development') {
          console.log('=== Respuesta recibida ===');
          console.log('Tiempo de respuesta:', fetchDuration, 'ms');
          console.log('Status:', response.status, response.statusText);
          console.log('URL:', fullUrl);
          console.log('Response OK:', response.ok);
          console.log('Headers CORS:', {
            'access-control-allow-origin': response.headers.get('access-control-allow-origin'),
            'access-control-allow-credentials': response.headers.get('access-control-allow-credentials'),
            'access-control-allow-methods': response.headers.get('access-control-allow-methods'),
            'content-type': response.headers.get('content-type')
          });
          console.log('Current origin:', typeof window !== 'undefined' ? window.location.origin : 'N/A');
        }
    } catch (fetchError) {
      // Si fetch falla, puede ser error de conexión o CORS
      clearTimeout(timeoutId);
      const currentOrigin = typeof window !== 'undefined' ? window.location.origin : 'unknown';
      
      // Logging detallado solo en desarrollo
      if (process.env.NODE_ENV === 'development') {
        console.error('=== Error en fetch ===');
        console.error('Error name:', fetchError.name);
        console.error('Error message:', fetchError.message);
        console.error('URL intentada:', fullUrl);
        console.error('Current origin:', currentOrigin);
        console.error('API_BASE_URL:', API_BASE_URL);
      }
      
      // Intentar verificar si el backend está realmente accesible
      // Hacer una prueba rápida con no-cors para ver si el servidor responde
      let backendAccessible = false;
      let backendTestError = null;
      
      try {
        // Esta petición no verifica CORS, solo si el servidor responde
        // Usamos un timeout corto para no bloquear
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 2000); // 2 segundos timeout
        
        const testResponse = await fetch(`${API_BASE_URL}/docs`, { 
          method: 'HEAD', 
          mode: 'no-cors',
          cache: 'no-cache',
          signal: controller.signal
        });
        clearTimeout(timeout);
        backendAccessible = true;
        if (process.env.NODE_ENV === 'development') {
          console.log('✓ Backend está accesible (pero puede ser problema de CORS)');
        }
      } catch (testError) {
        backendTestError = testError;
        if (process.env.NODE_ENV === 'development') {
          console.log('✗ Backend no responde:', testError.message);
          console.log('  Error name:', testError.name);
        }
        backendAccessible = false;
      }
      
      // Verificar si es un error de red REAL (no CORS)
      const isRealConnectionError = (
        fetchError.name === 'TypeError' && 
        (fetchError.message?.includes('Failed to fetch') || 
         fetchError.message?.includes('NetworkError') ||
         fetchError.message?.includes('ERR_CONNECTION_REFUSED') ||
         fetchError.message?.includes('ERR_NETWORK') ||
         fetchError.message?.includes('ERR_INTERNET_DISCONNECTED'))
      ) || fetchError.name === 'AbortError';
      
      // Si el backend NO está accesible, es un error de conexión real
      if (!backendAccessible) {
      let errorMsg = process.env.NODE_ENV === 'development'
        ? `No se pudo conectar con el servidor en ${API_BASE_URL}.\n\nOrigen actual: ${currentOrigin}\n\nEl backend NO está respondiendo.\n\nVerifica:\n1. Que el backend esté corriendo: http://127.0.0.1:8000\n2. Que el puerto 8000 no esté bloqueado por firewall\n3. Reinicia el backend si es necesario`
        : `No se pudo conectar con el servidor. Por favor, intente nuevamente.`;
        
        if (process.env.NODE_ENV === 'development') {
          console.error('Error de conexión:', errorMsg);
        }
        const connectionError = new Error(errorMsg);
        connectionError.name = 'ConnectionError';
        connectionError.isConnectionError = true;
        throw connectionError;
      }
      
      // Si el backend SÍ está accesible pero falla la petición, es probablemente CORS
      if (backendAccessible && isRealConnectionError) {
        let corsHint = `2. Agrega ${currentOrigin} a CORS_ALLOWED_ORIGINS en backend/config/settings/local.py\n   (o define CORS_ALLOWED_ORIGINS en backend/.env si usas production.py)`;
        const settingsModule = backendEnvHint?.settingsModule;
        const environment = backendEnvHint?.environment;
        if (environment === 'production' || (settingsModule && settingsModule.includes('production'))) {
          corsHint = `2. Define CORS_ALLOWED_ORIGINS en backend/.env (por ejemplo: http://localhost:3001)`;
        } else if (environment === 'local' || (settingsModule && settingsModule.includes('local'))) {
          corsHint = `2. Agrega ${currentOrigin} a CORS_ALLOWED_ORIGINS en backend/config/settings/local.py`;
        }
        let errorMsg = process.env.NODE_ENV === 'development'
          ? `Error de conexión con el backend.\n\nOrigen actual: ${currentOrigin}\nBackend URL: ${API_BASE_URL}\n\n⚠️ PROBLEMA DE CORS DETECTADO:\nEl backend está corriendo pero rechaza la conexión desde ${currentOrigin}.\n\nSOLUCIÓN (según settings activo):\n1. Verifica que el backend esté corriendo: http://127.0.0.1:8000\n${corsHint}\n3. Reinicia el backend después de cambiar la configuración`
          : `Error de conexión con el servidor. Por favor, contacte al administrador.`;
        
        if (process.env.NODE_ENV === 'development') {
          console.error('Error de CORS:', errorMsg);
        }
        const corsError = new Error(errorMsg);
        corsError.name = 'CorsError';
        corsError.isConnectionError = true; // Para que se muestre como error de conexión
        throw corsError;
      }
      
      // Para otros errores, lanzar el error original
      const errorMsg = fetchError.message || 'Error de conexión';
      const genericError = new Error(errorMsg);
      genericError.name = fetchError.name;
      genericError.originalError = fetchError;
      throw genericError;
    }
    
    clearTimeout(timeoutId);
    
    // Si la respuesta no es exitosa, lanzar error
    if (!response.ok) {
      // Log detallado del error solo en desarrollo
      // No loguear 404 para settings opcionales (como logout_timeout_minutes)
      const isOptionalSetting = normalizedEndpoint.includes('/settings/value/');
      if (process.env.NODE_ENV === 'development' && !(response.status === 404 && isOptionalSetting)) {
        console.error('=== Respuesta no exitosa ===');
        console.error('Status:', response.status, response.statusText);
        console.error('URL:', fullUrl);
        console.error('Method:', method);
      }
      // Manejar errores 401 (No autorizado - token expirado o credenciales inválidas)
      if (response.status === 401) {
        // Si es un endpoint de login, el 401 significa credenciales inválidas, no token expirado
        const isLoginEndpoint = normalizedEndpoint.includes('/auth/login/');
        const isRefreshEndpoint = normalizedEndpoint.includes('/auth/refresh/');
        
        if (isLoginEndpoint) {
          // Para login, obtener el mensaje de error del backend
          let errorMessage = 'Credenciales inválidas. Por favor, verifica tu email y contraseña.';
          
          // Verificar que la respuesta sea JSON antes de parsear
          const contentType = response.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            try {
              // Clonar la respuesta para poder leerla sin consumirla
              const responseClone = response.clone();
              const errorData = await responseClone.json();
              if (errorData.detail) {
                errorMessage = typeof errorData.detail === 'string' ? errorData.detail : errorMessage;
              } else if (errorData.message) {
                errorMessage = errorData.message;
              } else if (typeof errorData === 'string') {
                errorMessage = errorData;
              }
            } catch (e) {
              // Si no se puede parsear, usar mensaje por defecto
              if (process.env.NODE_ENV === 'development') {
                console.warn('No se pudo parsear el error de login:', e);
              }
            }
          }
          
          const loginError = new Error(errorMessage);
          loginError.name = 'LoginError';
          loginError.isAuthError = true;
          throw loginError;
        }

        if (!options._retry && !isRefreshEndpoint) {
          const refreshed = await refreshAccessToken();
          if (refreshed) {
            return await apiRequest(endpoint, { ...options, _retry: true });
          }
        }

        // Para otros endpoints, es un token expirado o inválido
        // Verificar si el token todavía existe antes de disparar el evento
        // Esto evita bucles infinitos cuando el logout falla con 401
        const tokenExists = !!localStorage.getItem('token');
        
        // Limpiar token expirado
        localStorage.removeItem('token');
        localStorage.removeItem('refresh_token');
        
        // Solo disparar el evento si el token existía (evita bucles cuando logout falla)
        if (tokenExists) {
          // Disparar evento personalizado para que AuthContext maneje el logout
          window.dispatchEvent(new CustomEvent('auth:token-expired'));
        }
        
        // Crear un error especial que puede ser identificado pero no se propaga como error no capturado
        const authError = new Error('Tu sesión ha expirado. Por favor, inicia sesión nuevamente.');
        authError.name = 'AuthenticationError';
        authError.isAuthError = true;
        throw authError;
      }
      
      let errorMessage = `Error ${response.status}: ${response.statusText}`;
      
      // Verificar que la respuesta sea JSON antes de parsear
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        // Si no es JSON, probablemente es HTML (404, etc.)
        const text = await response.text();
        if (process.env.NODE_ENV === 'development') {
          console.error('=== Respuesta de error no es JSON ===');
          console.error('Status:', response.status, response.statusText);
          console.error('Content-Type:', contentType);
          console.error('URL:', fullUrl);
          console.error('Respuesta (primeros 500 chars):', text.substring(0, 500));
        }
        
        // Si es HTML, probablemente es un 404 del servidor web
        if (text.trim().startsWith('<!') || text.trim().startsWith('<html')) {
          errorMessage = `Endpoint no encontrado (404): ${fullUrl}. Verifica que el endpoint esté correcto y que el backend esté corriendo.`;
        } else {
          errorMessage = `Respuesta inesperada del servidor (${response.status}): ${response.statusText}. Se esperaba JSON pero se recibió ${contentType || 'text/html'}`;
        }
        throw new Error(errorMessage);
      }
      
      try {
        const errorData = await response.json();
        // Manejar diferentes formatos de error
        if (typeof errorData === 'string') {
          errorMessage = errorData;
        } else if (Array.isArray(errorData)) {
          errorMessage = errorData.map(err => {
            if (typeof err === 'string') return err;
            return err.msg || err.message || JSON.stringify(err);
          }).join(', ');
        } else if (errorData && typeof errorData === 'object') {
          // Errores de validación por campo (ej: { email: ["..."], name: ["..."] }).
          // Priorizamos esta info sobre 'detail' porque 'detail' es solo un resumen
          // genérico agregado por el backend y pierde el nombre del campo que falló.
          const metaKeys = ['detail', 'type', 'message'];
          const fieldKeys = Object.keys(errorData).filter((k) => !metaKeys.includes(k));
          if (fieldKeys.length > 0) {
            const fieldErrors = {};
            fieldKeys.forEach((k) => { fieldErrors[k] = errorData[k]; });
            errorMessage = JSON.stringify(fieldErrors);
          } else if (errorData.detail) {
            if (typeof errorData.detail === 'string') {
              errorMessage = errorData.detail;
            } else if (Array.isArray(errorData.detail)) {
              errorMessage = errorData.detail.map(err => {
                if (typeof err === 'string') return err;
                if (err.msg) return err.msg;
                if (err.message) return err.message;
                if (err.loc && err.msg) return `${err.loc.join('.')}: ${err.msg}`;
                return JSON.stringify(err);
              }).join(', ');
            } else {
              errorMessage = errorData.detail.message || errorData.detail.msg || JSON.stringify(errorData.detail);
            }
          } else if (errorData.message) {
            errorMessage = String(errorData.message);
          } else {
            try {
              errorMessage = JSON.stringify(errorData);
            } catch {
              errorMessage = `Error ${response.status}: ${response.statusText}`;
            }
          }
        }
      } catch (parseError) {
        // Si no se puede parsear el error, usar el mensaje por defecto
        if (process.env.NODE_ENV === 'development') {
          console.error('Error parsing error response:', parseError);
        }
      }
      throw new Error(String(errorMessage));
    }

    // Si la respuesta está vacía (204 No Content), retornar null
    if (response.status === 204) {
      return null;
    }

    // Verificar que la respuesta sea JSON antes de parsear
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      // Si no es JSON, probablemente es HTML (404, etc.)
      const text = await response.text();
      if (process.env.NODE_ENV === 'development') {
        console.error('=== Respuesta no es JSON ===');
        console.error('Status:', response.status, response.statusText);
        console.error('Content-Type:', contentType);
        console.error('URL:', fullUrl);
        console.error('Respuesta (primeros 500 chars):', text.substring(0, 500));
      }
      
      // Si es HTML, probablemente es un 404 del servidor web
      if (text.trim().startsWith('<!') || text.trim().startsWith('<html')) {
        throw new Error(`Endpoint no encontrado (404): ${fullUrl}. Verifica que el endpoint esté correcto y que el backend esté corriendo.`);
      }
      
      throw new Error(`Respuesta inesperada del servidor (${response.status}): ${response.statusText}. Se esperaba JSON pero se recibió ${contentType || 'text/html'}`);
    }

    try {
      const data = await response.json();

      // Normalización opcional para endpoints de listado:
      // soporta array directo o formato paginado { count, results }.
      if (options.normalizeListResponse === true) {
        if (Array.isArray(data)) return data;
        if (Array.isArray(data?.results)) return data.results;
        return [];
      }

      return data;
    } catch (jsonError) {
      // Si falla el parseo JSON, loggear el error
      if (process.env.NODE_ENV === 'development') {
        console.error('=== Error parseando JSON ===');
        console.error('Error:', jsonError);
        console.error('Status:', response.status);
        console.error('URL:', fullUrl);
        const text = await response.text();
        console.error('Respuesta (primeros 500 chars):', text.substring(0, 500));
      }
      throw new Error(`Error parseando respuesta JSON: ${jsonError.message}. La respuesta del servidor no es JSON válido.`);
    }
  } catch (error) {
    // Logging detallado solo en desarrollo
    // No loguear errores 404 para settings opcionales (como logout_timeout_minutes)
    const isOptionalSetting = endpoint.includes('/settings/value/');
    const is404Error = error.message && (error.message.includes('404') || error.message.includes('no encontrado'));
    
    if (process.env.NODE_ENV === 'development' && !(isOptionalSetting && is404Error)) {
      console.error('=== API Request Error ===');
      console.error('Error name:', error.name);
      console.error('Error message:', error.message);
      console.error('Error completo:', error);
      console.error('URL intentada:', `${API_BASE_URL}${endpoint}`);
      console.error('API_BASE_URL configurado:', API_BASE_URL);
      console.error('Token disponible:', !!localStorage.getItem('token'));
      console.error('Token value:', localStorage.getItem('token') ? localStorage.getItem('token').substring(0, 20) + '...' : 'null');
      console.error('Current origin:', typeof window !== 'undefined' ? window.location.origin : 'N/A');
      console.error('Endpoint:', endpoint);
      
      // Diagnóstico adicional para errores de conexión (solo en desarrollo)
      if (error.isConnectionError || error.name === 'ConnectionError') {
        console.log('=== Diagnóstico de conexión ===');
        console.log('Verificando si el backend responde...');
        // Intentar verificar si el backend está accesible (solo en desarrollo)
        if (typeof window !== 'undefined') {
          detectBackendEnvironment().finally(() => {
            const settingsModule = backendEnvHint?.settingsModule;
            const environment = backendEnvHint?.environment;
            if (settingsModule || environment) {
              const hint = environment || settingsModule;
              console.log(`✓ Entorno detectado desde /health: ${hint}`);
              if (environment === 'production' || (settingsModule && settingsModule.includes('production'))) {
                console.log('   Ajusta CORS_ALLOWED_ORIGINS en backend/.env (production.py)');
              } else {
                console.log('   Ajusta CORS_ALLOWED_ORIGINS en backend/config/settings/local.py');
              }
            }
          });
          fetch(`${API_BASE_URL}/docs`, { method: 'HEAD', mode: 'no-cors' })
            .then(() => {
              console.log('✓ Backend parece estar accesible (pero puede ser problema de CORS)');
              console.log('⚠️ Si el backend responde pero falla la petición, puede ser un problema de CORS');
              console.log(`   Verifica que ${window.location.origin} esté en CORS_ALLOWED_ORIGINS del backend`);
            })
            .catch(() => {
              console.log('✗ Backend no responde en', API_BASE_URL);
              console.log('   Asegúrate de que el backend esté corriendo');
            });
        }
      }
    }
    
    // Manejar errores de red específicos
    if (error.name === 'AbortError') {
      throw new Error('La petición tardó demasiado. Verifica tu conexión a internet.');
    } else if (error.isConnectionError) {
      // Si ya es un error de conexión marcado, re-lanzarlo
      throw error;
    } else if (error.name === 'ConnectionError') {
      // Si es un error de conexión, re-lanzarlo
      throw error;
    } else if (error.message === 'Failed to fetch' || (error.name === 'TypeError' && error.message?.includes('Failed to fetch'))) {
      // Puede ser CORS o conexión real - crear error de conexión
      const currentOrigin = typeof window !== 'undefined' ? window.location.origin : 'unknown';
      const errorMsg = process.env.NODE_ENV === 'development'
        ? `No se pudo conectar con el servidor en ${API_BASE_URL}. Verifica que el backend esté corriendo. Abre http://127.0.0.1:8000 en tu navegador para verificar.`
        : `No se pudo conectar con el servidor. Por favor, intente nuevamente.`;
      const connectionError = new Error(errorMsg);
      connectionError.name = 'ConnectionError';
      connectionError.isConnectionError = true;
      throw connectionError;
    }
    
    // Asegurar que siempre se lance un Error con mensaje
    if (error instanceof Error) {
      throw error;
    } else {
      throw new Error(error?.toString() || 'Error desconocido en la petición');
    }
  }
};

// Métodos HTTP helpers
export const api = {
  get: (endpoint, options) => apiRequest(endpoint, { ...options, method: 'GET' }),
  post: (endpoint, data, options) => apiRequest(endpoint, { ...options, method: 'POST', body: data }),
  put: (endpoint, data, options) => apiRequest(endpoint, { ...options, method: 'PUT', body: data }),
  patch: (endpoint, data, options) => apiRequest(endpoint, { ...options, method: 'PATCH', body: data }),
  delete: (endpoint, options) => apiRequest(endpoint, { ...options, method: 'DELETE' }),
};

export default API_BASE_URL;

